import React, { useState, useEffect } from 'react';
import { X, User, MapPin, Clock, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import './WorkerAssignmentModal.css';

const WorkerAssignmentModal = ({ isOpen, onClose, report, onAssignSuccess }) => {
  const [workers, setWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchAvailableWorkers();
    }
  }, [isOpen]);

  const fetchAvailableWorkers = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('binbuddy_token');
      const response = await axios.get('/api/reports/workers/available', {
        headers: { Authorization: `Bearer ${token}` }
      });

      setWorkers(response.data.workers || []);
    } catch (err) {
      console.error('Error fetching workers:', err);
      
      if (err.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else if (err.response?.status === 403) {
        setError('Access denied. Admin privileges required.');
      } else if (err.response?.status === 404) {
        setError('API endpoint not found. Please check server configuration.');
      } else {
        setError(`Failed to load available workers: ${err.response?.data?.message || err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAssignWorker = async () => {
    if (!selectedWorker) {
      setError('Please select a worker to assign.');
      return;
    }

    try {
      setAssigning(true);
      setError('');

      const token = localStorage.getItem('binbuddy_token');
      const response = await axios.post(
        `/api/reports/${report._id}/assign-enhanced`,
        { workerId: selectedWorker._id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Call success callback
      if (onAssignSuccess) {
        onAssignSuccess(response.data);
      }

      // Close modal
      onClose();
      
      // Reset state
      setSelectedWorker(null);
      setWorkers([]);

    } catch (err) {
      console.error('Error assigning worker:', err);
      setError(err.response?.data?.message || 'Failed to assign worker. Please try again.');
    } finally {
      setAssigning(false);
    }
  };

  const handleClose = () => {
    if (!assigning) {
      setSelectedWorker(null);
      setError('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="worker-assignment-modal">
        {/* Header */}
        <div className="modal-header">
          <h2>Assign Worker</h2>
          <button 
            className="close-button" 
            onClick={handleClose}
            disabled={assigning}
          >
            <X size={20} />
          </button>
        </div>

        {/* Report Info */}
        <div className="report-info">
          <h3>Report Details</h3>
          <div className="report-summary">
            <div className="info-item">
              <AlertCircle size={16} />
              <span>{report?.issueType?.replace('_', ' ') || 'Unknown Issue'}</span>
            </div>
            <div className="info-item">
              <MapPin size={16} />
              <span>{report?.location?.address || 'Location not available'}</span>
            </div>
            <div className="info-item">
              <Clock size={16} />
              <span>Deadline: 30 minutes after assignment</span>
            </div>
          </div>
          <div className={`priority-badge ${report?.priority || 'medium'}`}>
            {report?.priority || 'Medium'} Priority
          </div>
        </div>

        {/* Workers List */}
        <div className="workers-section">
          <h3>Available Workers</h3>
          
          {loading && (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading available workers...</p>
            </div>
          )}

          {error && (
            <div className="error-message">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && workers.length === 0 && (
            <div className="no-workers">
              <User size={24} />
              <p>No workers are currently available.</p>
              <button onClick={fetchAvailableWorkers} className="retry-button">
                Refresh List
              </button>
            </div>
          )}

          {!loading && workers.length > 0 && (
            <div className="workers-list">
              {workers.map((worker) => (
                <div
                  key={worker._id}
                  className={`worker-card ${selectedWorker?._id === worker._id ? 'selected' : ''}`}
                  onClick={() => setSelectedWorker(worker)}
                >
                  <div className="worker-info">
                    <div className="worker-header">
                      <div className="worker-name">
                        <User size={18} />
                        <span>{worker.name}</span>
                      </div>
                      <div className="worker-status">
                        <div className="status-indicator active"></div>
                        <span>Available</span>
                      </div>
                    </div>
                    
                    <div className="worker-details">
                      <div className="detail-item">
                        <span className="label">Phone:</span>
                        <span>{worker.phone || 'Not provided'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Zone:</span>
                        <span>{worker.zone || 'Not assigned'}</span>
                      </div>
                      {worker.currentLocation && (
                        <div className="detail-item">
                          <MapPin size={14} />
                          <span>Current location available</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {selectedWorker?._id === worker._id && (
                    <div className="selected-indicator">
                      <CheckCircle size={20} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Assignment Info */}
        {selectedWorker && (
          <div className="assignment-info">
            <h4>Assignment Details</h4>
            <div className="assignment-summary">
              <div className="summary-item">
                <span className="label">Worker:</span>
                <span>{selectedWorker.name}</span>
              </div>
              <div className="summary-item">
                <span className="label">Task Deadline:</span>
                <span>30 minutes from assignment</span>
              </div>
              <div className="summary-item">
                <span className="label">Required Actions:</span>
                <span>QR Scan → GPS Verify → Photo Capture</span>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="modal-actions">
          <button 
            className="cancel-button" 
            onClick={handleClose}
            disabled={assigning}
          >
            Cancel
          </button>
          <button 
            className="assign-button" 
            onClick={handleAssignWorker}
            disabled={!selectedWorker || assigning}
          >
            {assigning ? (
              <>
                <div className="button-spinner"></div>
                Assigning...
              </>
            ) : (
              'Assign Worker'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkerAssignmentModal;
